import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { OverlayPanel } from "primereact/overlaypanel";
import { formatVolume } from "../../utils/numberFormatting";
import { useSuperDomAutoCenter } from "./hooks/useSuperDomAutoCenter";
import { useDepthBarCanvas } from "./hooks/useDepthBarCanvas";
import { addSyntheticAnnotationRows } from "./hooks/bookByPriceHelpers";
import { orderModifierForSide } from "./hooks/orderModifierSide";
import {
  BookPriceCell,
  getBookAnnotationsForPrice,
  getBookPriceKey,
  getFramableAnnotations,
} from "./BookByPriceAnnotations";
import BookOrderStackPanel from "./BookOrderStackPanel";
import BookOrderPreview from "./BookOrderPreview";
import { useBookOrderPreview } from "./hooks/useBookOrderPreview";
import useHostDocument from "~/hooks/useHostDocument";

const BID_BAR = "rgba(50,80,220,0.35)";
const ASK_BAR = "rgba(200,40,50,0.35)";
// OTO connector lanes — overlapping same-side spines offset into parallel
// lines; capped at 3 lanes (0–2), each stepped further into the price column.
const MAX_BRACKET_LANE = 2;
const BRACKET_LANE_STEP_PX = 3;
const isOrderModifier = (event) =>
  event.shiftKey || event.ctrlKey || event.metaKey;
const STATE_PRIORITY = ["open", "partial", "pending", "local"];
const normalizeOrderState = (state) => {
  const s = String(state || "open").toLowerCase();
  if (s === "partiallyfilled") return "partial";
  if (STATE_PRIORITY.includes(s)) return s;
  return "open";
};
const getDominantState = (annotations, predicate) => {
  const states = annotations
    .filter(predicate)
    .map((annotation) => normalizeOrderState(annotation.state));
  return STATE_PRIORITY.find((state) => states.includes(state)) || null;
};
const getOtoDetail = (annotations, side) =>
  annotations.find(
    (annotation) =>
      annotation.side === side &&
      (annotation.kind === "loss" || annotation.kind === "profit") &&
      annotation.detail,
  )?.detail || "";

const BookByPriceSuperDOMView = ({
  contentRef,
  symbol,
  ladder = [],
  bestBid,
  bestAsk,
  depth,
  viewMode,
  status,
  freezeAutoCenter,
  annotationsByPrice = {},
  onOrder,
  onAnnotationPointerDown,
  onStackPointerDown,
  onAnnotationCancel,
  onMoveOrders,
  isDragging = false,
  draggingOrderIds = null,
  interactionDisabled = false,
}) => {
  // PoC #popout: document the ladder currently lives in (portals/appendTo)
  const [hostDoc, hostProbe] = useHostDocument();
  const stackOverlayRef = useRef(null);
  const stackAnchorRef = useRef(null);
  const closeTimerRef = useRef(null);
  const tableRef = useRef(null);
  const depthCanvasRef = useRef(null);
  const [stackPrice, setStackPrice] = useState(null);
  const [stackOpen, setStackOpen] = useState(false);

  // Open the stacked-orders panel anchored to the clicked aggregate cell.
  const handleStackOpen = (event, price) => {
    stackAnchorRef.current = event.currentTarget;
    setStackPrice(price);
    stackOverlayRef.current?.toggle(event);
  };

  // Anchor the panel to the cell that opened it — centred under the cell,
  // flipped above when it would overflow the bottom, clamped to the viewport.
  // Overrides OverlayPanel's default left-align + overflow-shift, which could
  // leave the panel detached far to the left of the cell.
  const positionStackOverlay = useCallback(() => {
    const anchor = stackAnchorRef.current;
    // This instance's own overlay element (getElement === the .bbp-stack-overlay
    // root). A document-global querySelector would resolve to the FIRST overlay
    // in the DOM, so with two DoM panels open it would reposition the wrong one.
    const panel = stackOverlayRef.current?.getElement();
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    const margin = 8;
    // PoC #popout: clamp against the ladder's own window viewport.
    const ownerDoc = anchor.ownerDocument ?? document;
    const vw = ownerDoc.documentElement.clientWidth;
    const vh = ownerDoc.documentElement.clientHeight;
    let left = a.left + a.width / 2 - p.width / 2;
    left = Math.max(margin, Math.min(left, vw - p.width - margin));
    let top = a.bottom + 4;
    if (top + p.height > vh - margin) {
      const above = a.top - p.height - 4;
      top = above >= margin ? above : Math.max(margin, vh - p.height - margin);
    }
    panel.style.left = `${Math.round(left + window.scrollX)}px`;
    panel.style.top = `${Math.round(top + window.scrollY)}px`;
  }, []);

  // While open, dismiss only once the cursor leaves a generous dead-zone — the
  // panel plus a margin of its own size on every side (and the anchor cell).
  // Pointer-down beyond the zone closes at once; a move beyond it closes after
  // a short grace delay. Scroll/resize re-anchors the panel to its cell.
  useEffect(() => {
    if (!stackOpen) return undefined;

    const clearCloseTimer = () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
    // Geometric dead-zone: the panel's rect expanded on every side by the
    // panel's own size (width L/R, height T/B), plus the anchor cell.
    const isInsideZone = (x, y) => {
      // Scope to THIS instance's overlay (not a document-global lookup that
      // would grab another open DoM's panel and measure the wrong rect).
      const panel = stackOverlayRef.current
        ?.getElement()
        ?.querySelector(".bbp-stack-panel");
      if (panel) {
        const r = panel.getBoundingClientRect();
        if (
          x >= r.left - r.width &&
          x <= r.right + r.width &&
          y >= r.top - r.height &&
          y <= r.bottom + r.height
        ) {
          return true;
        }
      }
      const a = stackAnchorRef.current?.getBoundingClientRect?.();
      if (a && x >= a.left && x <= a.right && y >= a.top && y <= a.bottom) {
        return true;
      }
      // Panel not yet measurable → keep open rather than flicker closed.
      return !panel;
    };
    const handleOutsidePointer = (event) => {
      if (!isInsideZone(event.clientX, event.clientY)) {
        stackOverlayRef.current?.hide();
      }
    };
    const handlePointerMove = (event) => {
      if (isInsideZone(event.clientX, event.clientY)) {
        clearCloseTimer();
      } else if (!closeTimerRef.current) {
        closeTimerRef.current = setTimeout(() => {
          closeTimerRef.current = null;
          stackOverlayRef.current?.hide();
        }, 260);
      }
    };
    // PoC #popout: listen in the window the ladder actually lives in.
    const doc = tableRef.current?.ownerDocument ?? document;
    const win = doc.defaultView ?? window;
    doc.addEventListener("pointerdown", handleOutsidePointer);
    doc.addEventListener("pointermove", handlePointerMove);
    win.addEventListener("scroll", positionStackOverlay, true);
    win.addEventListener("resize", positionStackOverlay);
    return () => {
      clearCloseTimer();
      doc.removeEventListener("pointerdown", handleOutsidePointer);
      doc.removeEventListener("pointermove", handlePointerMove);
      win.removeEventListener("scroll", positionStackOverlay, true);
      win.removeEventListener("resize", positionStackOverlay);
    };
  }, [stackOpen, positionStackOverlay]);

  // Re-derived from live annotations so reprices/cancels reflect immediately.
  const stackEntries = useMemo(
    () =>
      getFramableAnnotations(
        getBookAnnotationsForPrice(annotationsByPrice, symbol, stackPrice),
      ),
    [annotationsByPrice, symbol, stackPrice],
  );
  const displayLadder = useMemo(
    () => addSyntheticAnnotationRows(ladder, annotationsByPrice),
    [annotationsByPrice, ladder],
  );

  // Price keys present in the rendered ladder — an OTO leg whose key is here
  // is drawn on its row; one that is not falls back to the cursor badge.
  const ladderKeys = useMemo(() => {
    const keys = new Set();
    for (const row of displayLadder) {
      const key = getBookPriceKey(symbol, row.price);
      if (key) keys.add(key);
    }
    return keys;
  }, [displayLadder, symbol]);

  const { previewByPrice, badge } = useBookOrderPreview({
    tableRef,
    symbol,
    ladderKeys,
    disabled: interactionDisabled || isDragging,
  });

  useSuperDomAutoCenter({
    contentRef,
    ladder: displayLadder,
    bestBid,
    bestAsk,
    symbol,
    depth,
    viewMode,
    status,
    freezeAutoCenter,
  });

  const maxQty = useMemo(() => {
    let m = 0;
    for (const row of displayLadder) {
      if (row.bid?.quantity > m) m = row.bid.quantity;
      if (row.ask?.quantity > m) m = row.ask.quantity;
    }
    return m;
  }, [displayLadder]);

  // Depth bars are painted on a single canvas layer behind the table instead
  // of a per-<tr> linear-gradient (the measured paint hotpath).
  useDepthBarCanvas({
    canvasRef: depthCanvasRef,
    tableRef,
    rows: displayLadder,
    maxQty,
    bidColor: BID_BAR,
    askColor: ASK_BAR,
  });

  // OTO connectors — a bracket down the PREÇO column tying an order's main
  // row to its in-grid ganho/perda rows: a vertical spine on the price
  // column's edge next to the main's qty column, plus a horizontal arm
  // crossing to each leg row. Drawn for the hover preview and for every
  // placed order that has OTO legs.
  const brackets = useMemo(() => {
    const rowIndexByKey = new Map();
    displayLadder.forEach((row, i) => {
      const key = getBookPriceKey(symbol, row.price);
      if (key) rowIndexByKey.set(key, i);
    });

    const makeBracket = (
      key,
      variant,
      mainIdx,
      mainSide,
      profitIdx,
      lossIdx,
      dashed,
    ) => {
      const rows = [mainIdx, profitIdx, lossIdx].filter((idx) => idx >= 0);
      return {
        key,
        variant,
        top: Math.min(...rows),
        bottom: Math.max(...rows),
        mainIdx,
        mainSide,
        profitIdx,
        lossIdx,
        dashed,
        // Preview: spine next to the entry's qty column. Placed order: spine
        // on the price column edge toward the LEGS' column — where the main
        // ends and the ganho/perda orders begin (a buy's legs sit on the ask,
        // a sell's on the bid). buy/sell flips the side accordingly.
        spineSide:
          variant === "order"
            ? mainSide === "buy"
              ? "right"
              : "left"
            : mainSide === "buy"
              ? "left"
              : "right",
      };
    };

    const result = [];

    // Hover preview — a not-yet-placed order, so always dashed.
    if (previewByPrice) {
      let entryIdx = -1;
      let entrySide = null;
      let profitIdx = -1;
      let lossIdx = -1;
      Object.entries(previewByPrice).forEach(([key, mark]) => {
        const i = rowIndexByKey.get(key);
        if (i === undefined) return;
        if (mark.kind === "entry") {
          entryIdx = i;
          entrySide = mark.side;
        } else if (mark.kind === "profit") {
          profitIdx = i;
        } else if (mark.kind === "loss") {
          lossIdx = i;
        }
      });
      if (entryIdx >= 0 && (profitIdx >= 0 || lossIdx >= 0)) {
        result.push(
          makeBracket(
            "preview",
            "preview",
            entryIdx,
            entrySide,
            profitIdx,
            lossIdx,
            true,
          ),
        );
      }
    }

    // Placed orders with OTO legs — group the order + leg annotations by
    // orderId, then connect each group that has a main and an in-grid leg.
    const byOrder = new Map();
    displayLadder.forEach((row, i) => {
      const annotations = getBookAnnotationsForPrice(
        annotationsByPrice,
        symbol,
        row.price,
      );
      annotations.forEach((a) => {
        if (!a.orderId) return;
        const g = byOrder.get(a.orderId) || {
          mainIdx: -1,
          profitIdx: -1,
          lossIdx: -1,
        };
        if (a.kind === "order") {
          g.mainIdx = i;
          g.mainSide = a.side;
          g.state = a.state;
        } else if (a.kind === "profit") {
          g.profitIdx = i;
        } else if (a.kind === "loss") {
          g.lossIdx = i;
        }
        byOrder.set(a.orderId, g);
      });
    });
    byOrder.forEach((g, orderId) => {
      if (g.mainIdx < 0 || (g.profitIdx < 0 && g.lossIdx < 0)) return;
      // Line style follows the order state — dashed while not yet live
      // (local/pending), solid once open, matching the order's frame.
      const dashed = g.state === "local" || g.state === "pending";
      result.push(
        makeBracket(
          `order:${orderId}`,
          "order",
          g.mainIdx,
          g.mainSide,
          g.profitIdx,
          g.lossIdx,
          dashed,
        ),
      );
    });

    // Lanes — placed-order spines that overlap on the same side run as
    // parallel lines (offset inward) instead of merging. Greedy interval
    // colouring, capped at MAX_BRACKET_LANE. The preview stays at lane 0.
    result.forEach((b) => {
      b.lane = 0;
    });
    ["left", "right"].forEach((side) => {
      const group = result
        .filter((b) => b.variant === "order" && b.spineSide === side)
        .sort((a, b) => a.top - b.top || a.bottom - b.bottom);
      const laneEnds = []; // laneEnds[k] = bottom row of lane k's last bracket
      group.forEach((b) => {
        let lane = laneEnds.findIndex((end) => end < b.top);
        if (lane === -1) {
          if (laneEnds.length <= MAX_BRACKET_LANE) {
            lane = laneEnds.length;
            laneEnds.push(b.bottom);
          } else {
            // Past the cap — reuse the innermost lane (rare: 4+ overlapping).
            lane = MAX_BRACKET_LANE;
            laneEnds[lane] = Math.max(laneEnds[lane], b.bottom);
          }
        } else {
          laneEnds[lane] = b.bottom;
        }
        b.lane = lane;
      });
    });

    return result;
  }, [previewByPrice, annotationsByPrice, displayLadder, symbol]);

  // Per-row bracket pieces — one segment per bracket covering this row.
  const bracketSegmentsAt = (i) => {
    const segments = [];
    for (const b of brackets) {
      if (i < b.top || i > b.bottom) continue;
      // Each segment takes a side colour (buy/sell), matching the chart: the
      // main row its own side, the leg rows the opposite (legs are
      // opposite-side orders).
      const tone =
        i === b.mainIdx ? b.mainSide : b.mainSide === "buy" ? "sell" : "buy";
      const isPreview = b.variant === "preview";
      const isLeg = i === b.profitIdx || i === b.lossIdx;
      const isMoldura = isLeg || i === b.mainIdx;
      segments.push({
        key: b.key,
        tone,
        spineSide: b.spineSide,
        dashed: b.dashed,
        lane: b.lane,
        // Preview: an arm crossing the column to the leg's ghost cell.
        // Placed order: a short stub linking the spine to each moldura.
        arm: isPreview ? (isLeg ? "cross" : null) : isMoldura ? "stub" : null,
        // Trim the spine to the arm junction only for the preview — a placed
        // order's spine has no arm ends, so it runs the full rows.
        trimTop:
          isPreview &&
          i === b.top &&
          (b.top === b.profitIdx || b.top === b.lossIdx),
        trimBottom:
          isPreview &&
          i === b.bottom &&
          (b.bottom === b.profitIdx || b.bottom === b.lossIdx),
      });
    }
    return segments;
  };

  // One bracket segment: a spine slice (trimmed to the arm junction at the
  // preview's bracket ends) plus its arm — two halves split around the price
  // for the preview, or a short stub to the moldura for a placed order.
  const renderBracketSegment = (seg) => {
    const tone = `bbp-ladder__bracket--${seg.tone}`;
    const solid = seg.dashed ? "" : "bbp-ladder__bracket--solid";
    const armClass = (modifier) =>
      ["bbp-ladder__bracket-arm", modifier, tone, solid]
        .filter(Boolean)
        .join(" ");
    // Offset overlapping same-side spines into parallel lanes.
    const laneStyle = seg.lane
      ? { "--bbp-bracket-lane-offset": `${seg.lane * BRACKET_LANE_STEP_PX}px` }
      : undefined;
    return (
      <React.Fragment key={seg.key}>
        <span
          className={[
            "bbp-ladder__bracket",
            `bbp-ladder__bracket--spine-${seg.spineSide}`,
            tone,
            solid,
            seg.trimTop ? "bbp-ladder__bracket--trim-top" : "",
            seg.trimBottom ? "bbp-ladder__bracket--trim-bottom" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={laneStyle}
          aria-hidden="true"
        />
        {seg.arm === "cross" ? (
          <>
            <span
              className={armClass("bbp-ladder__bracket-arm--left")}
              aria-hidden="true"
            />
            <span
              className={armClass("bbp-ladder__bracket-arm--right")}
              aria-hidden="true"
            />
          </>
        ) : seg.arm === "stub" ? (
          <span
            className={armClass(
              `bbp-ladder__bracket-arm--stub-${seg.spineSide}`,
            )}
            style={laneStyle}
            aria-hidden="true"
          />
        ) : null}
      </React.Fragment>
    );
  };

  const handleOrderClick = (event, side, price) => {
    // Shift = buy column, Ctrl/Cmd = sell column (chart convention). Cross combos
    // (Shift on sell, Ctrl on buy) and plain clicks place nothing. (#344)
    if (!orderModifierForSide(event, side)) return;
    event.preventDefault();
    event.stopPropagation();
    onOrder?.({ side, price });
  };

  const handleOrderMouseDown = (event) => {
    if (!isOrderModifier(event)) return;
    event.preventDefault();
  };

  return (
    <>
      {hostProbe}
      <canvas
        ref={depthCanvasRef}
        className="bbp-depth-canvas"
        aria-hidden="true"
      />
      <table
        ref={tableRef}
        className={`bbp-ladder${isDragging ? " bbp-ladder--dragging" : ""}${interactionDisabled ? " bbp-ladder--disabled" : ""}`}
        data-evo-id="bookbypricepanel-ladder-superdom"
      >
        <colgroup>
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th>Compra</th>
            <th>Preço</th>
            <th>Venda</th>
          </tr>
        </thead>
        <tbody>
          {displayLadder.map((row, i) => {
            const annotations = getBookAnnotationsForPrice(
              annotationsByPrice,
              symbol,
              row.price,
            );
            // One tint per side — any order/leg presence colours the cell by
            // its side (buy = blue, sell = red), matching the chart.
            const isSidedOrder = (annotation, side) =>
              (annotation.kind === "order" ||
                annotation.kind === "loss" ||
                annotation.kind === "profit") &&
              annotation.side === side;
            const bidState = getDominantState(annotations, (a) =>
              isSidedOrder(a, "buy"),
            );
            const askState = getDominantState(annotations, (a) =>
              isSidedOrder(a, "sell"),
            );
            const bidOtoDetail = getOtoDetail(annotations, "buy");
            const askOtoDetail = getOtoDetail(annotations, "sell");
            const previewMark = previewByPrice
              ? previewByPrice[getBookPriceKey(symbol, row.price)]
              : null;
            const bidPreview =
              previewMark && previewMark.side === "buy" ? previewMark : null;
            const askPreview =
              previewMark && previewMark.side === "sell" ? previewMark : null;
            const bracketSegments = bracketSegmentsAt(i);
            const rowClass = [
              row.isBestBid ? "bbp-ladder__row--best-bid" : "",
              row.isBestAsk ? "bbp-ladder__row--best-ask" : "",
              row.isSyntheticAnnotation ? "bbp-ladder__row--synthetic" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <tr
                key={
                  row.isSyntheticAnnotation
                    ? `synthetic-${row.price}`
                    : `${row.price}-${i}`
                }
                className={rowClass}
                data-bbp-anchor={row.isAnchor ? "true" : undefined}
                data-bbp-price={row.price}
              >
                <td
                  className={[
                    "bbp-ladder__col-bid",
                    "bbp-ladder__trade-cell",
                    bidState ? `bbp-ladder__trade-cell--order-${bidState}` : "",
                    bidPreview ? "bbp-ladder__trade-cell--preview-buy" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onMouseDown={handleOrderMouseDown}
                  onClick={(event) => handleOrderClick(event, "buy", row.price)}
                  data-evo-id="bookbypricepanel-superdom-cell-buy"
                >
                  {bidPreview ? (
                    <span
                      className="bbp-ladder__preview-label"
                      title={bidPreview.title}
                    >
                      {bidPreview.label}
                    </span>
                  ) : bidOtoDetail ? (
                    <span
                      className="bbp-ladder__oto-detail"
                      title={bidOtoDetail}
                    >
                      {bidOtoDetail}
                    </span>
                  ) : row.bid ? (
                    `${formatVolume(row.bid.quantity)} (${formatVolume(row.bid.orders)})`
                  ) : (
                    ""
                  )}
                </td>
                <td
                  className={[
                    "bbp-ladder__col-price",
                    row.bid
                      ? "bbp-ladder__col-price--bid"
                      : row.ask
                        ? "bbp-ladder__col-price--ask"
                        : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {bracketSegments.map(renderBracketSegment)}
                  <BookPriceCell
                    symbol={symbol}
                    price={row.price}
                    annotations={annotations}
                    align="center"
                    onAnnotationPointerDown={onAnnotationPointerDown}
                    onStackPointerDown={onStackPointerDown}
                    onAnnotationCancel={onAnnotationCancel}
                    onStackOpen={handleStackOpen}
                    draggingOrderIds={draggingOrderIds}
                  />
                </td>
                <td
                  className={[
                    "bbp-ladder__col-ask",
                    "bbp-ladder__trade-cell",
                    askState ? `bbp-ladder__trade-cell--order-${askState}` : "",
                    askPreview ? "bbp-ladder__trade-cell--preview-sell" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onMouseDown={handleOrderMouseDown}
                  onClick={(event) =>
                    handleOrderClick(event, "sell", row.price)
                  }
                  data-evo-id="bookbypricepanel-superdom-cell-sell"
                >
                  {askPreview ? (
                    <span
                      className="bbp-ladder__preview-label"
                      title={askPreview.title}
                    >
                      {askPreview.label}
                    </span>
                  ) : askOtoDetail ? (
                    <span
                      className="bbp-ladder__oto-detail"
                      title={askOtoDetail}
                    >
                      {askOtoDetail}
                    </span>
                  ) : row.ask ? (
                    `${formatVolume(row.ask.quantity)} (${formatVolume(row.ask.orders)})`
                  ) : (
                    ""
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <OverlayPanel
        ref={stackOverlayRef}
        className="bbp-stack-overlay"
        appendTo={(hostDoc ?? document).body}
        dismissable={false}
        onShow={() => {
          setStackOpen(true);
          positionStackOverlay();
          requestAnimationFrame(positionStackOverlay);
        }}
        onHide={() => setStackOpen(false)}
      >
        <BookOrderStackPanel
          symbol={symbol}
          price={stackPrice}
          entries={stackEntries}
          onMove={onMoveOrders}
          onCancel={onAnnotationCancel}
        />
      </OverlayPanel>
      <BookOrderPreview badge={badge} />
    </>
  );
};

export default React.memo(BookByPriceSuperDOMView);

const bookLevelShape = PropTypes.shape({
  price: PropTypes.number,
  quantity: PropTypes.number,
  orders: PropTypes.number,
});

const ladderRowShape = PropTypes.shape({
  price: PropTypes.number,
  bid: bookLevelShape,
  ask: bookLevelShape,
  isBestBid: PropTypes.bool,
  isBestAsk: PropTypes.bool,
  isAnchor: PropTypes.bool,
  isSyntheticAnnotation: PropTypes.bool,
});

BookByPriceSuperDOMView.propTypes = {
  contentRef: PropTypes.shape({ current: PropTypes.any }),
  symbol: PropTypes.string,
  ladder: PropTypes.arrayOf(ladderRowShape),
  bestBid: bookLevelShape,
  bestAsk: bookLevelShape,
  depth: PropTypes.number,
  viewMode: PropTypes.string,
  status: PropTypes.string,
  freezeAutoCenter: PropTypes.bool,
  annotationsByPrice: PropTypes.object,
  onOrder: PropTypes.func,
  onAnnotationPointerDown: PropTypes.func,
  onStackPointerDown: PropTypes.func,
  onAnnotationCancel: PropTypes.func,
  onMoveOrders: PropTypes.func,
  isDragging: PropTypes.bool,
  draggingOrderIds: PropTypes.arrayOf(PropTypes.string),
  interactionDisabled: PropTypes.bool,
};
